import React, { useState } from 'react';
import AuthForm from './ContactForm';
import { FiMail, FiPhone, FiClock } from 'react-icons/fi';
import BookEnquiryManager from '../../../services/home/BookEnquiryManager';

export default function ContactUs() {
  const initialState = {
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    pincode: '',
    email: '',
    contact: '',
    subject: '',
    message: ''
  };

  const [form, setForm] = useState(initialState);
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const validateForm = () => {
    const errors = {};
    const pincodeRegex = /^\d{6}$/;
    const phoneRegex = /^\d{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!form.firstName.trim()) errors.firstName = 'First name is required';
    if (!form.lastName.trim()) errors.lastName = 'Last name is required';
    if (!form.address.trim()) errors.address = 'Address is required';
    if (!form.city.trim()) errors.city = 'City is required';
    if (!pincodeRegex.test(form.pincode)) errors.pincode = 'Pincode must be exactly 6 digits';
    if (!phoneRegex.test(form.contact)) errors.contact = 'Mobile number must be exactly 10 digits';
    if (!emailRegex.test(form.email)) errors.email = 'Invalid email format';
    if (!form.subject) errors.subject = 'Subject is required';
    if (!form.message.trim()) errors.message = 'Message is required';

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setValidationErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError(null);
    setSubmitted(false);

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const response = await BookEnquiryManager.createEnquiry(form);
      if (response.responseCode === 201) {
        setSubmitted(true);
        setForm(initialState);
        setValidationErrors({});
      } else {
        setError(response.responseMessage || 'Something went wrong.');
      }
    } catch (err) {
      console.error(err);
      setError('Submission failed. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderError = field =>
    validationErrors[field] && (
      <p className="text-sm text-red-500 mt-1">{validationErrors[field]}</p>
    );

  return (
    <AuthForm
      title="Please fill up the form below to send us a message."
      imageSrc="/images/contactUs.svg"
      extraContentBelowImage={
        <>
          <p className="font-semibold text-base">Books At Doorsteps</p>
          <p className="text-sm leading-snug mt-4">
            Office No 1, First Floor, Chandrashila Complex,<br />
            NDA Road, Shivane Pune 411023
          </p>
          <div className="flex items-center space-x-2 mt-2 text-sm">
            <FiMail className="text-[#FF7236]" />
            <span>care@booksatdoorsteps.com</span>
          </div>
          <div className="flex items-center space-x-2 mt-1 text-sm">
            <FiPhone className="text-[#FF7236]" />
            <span>+91 8600040039</span>
          </div>
          <div className="flex items-center space-x-2 mt-1 text-sm">
            <FiClock className="text-[#FF7236]" />
            <span>9:00 AM to 6:00 PM</span>
          </div>
        </>
      }
    >
      <form className="space-y-3" onSubmit={handleSubmit} noValidate>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <input
              type="text"
              name="firstName"
              placeholder="First Name"
              value={form.firstName}
              onChange={handleChange}
              className="border border-gray-300 rounded px-4 py-2 w-full"
            />
            {renderError('firstName')}
          </div>
          <div>
            <input
              type="text"
              name="lastName"
              placeholder="Last Name"
              value={form.lastName}
              onChange={handleChange}
              className="border border-gray-300 rounded px-4 py-2 w-full"
            />
            {renderError('lastName')}
          </div>
        </div>

        <div>
          <input
            type="text"
            name="address"
            placeholder="Address"
            value={form.address}
            onChange={handleChange}
            className="border border-gray-300 rounded px-4 py-2 w-full"
          />
          {renderError('address')}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <input
              type="text"
              name="city"
              placeholder="City"
              value={form.city}
              onChange={handleChange}
              className="border border-gray-300 rounded px-4 py-2 w-full"
            />
            {renderError('city')}
          </div>
          <div>
            <input
              type="text"
              name="pincode"
              placeholder="Pincode"
              value={form.pincode}
              onChange={handleChange}
              className="border border-gray-300 rounded px-4 py-2 w-full"
            />
            {renderError('pincode')}
          </div>
        </div>
        <div>
            <input
              type="text"
              name="contact"
              placeholder="Mobile Number"
              value={form.contact}
              onChange={handleChange}
              className="border border-gray-300 rounded px-4 py-2 w-full"
            />
            {renderError('contact')}
          </div>
        <div>
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={form.email}
            onChange={handleChange}
            className="border border-gray-300 rounded px-4 py-2 w-full"
          />
          {renderError('email')}
        </div>

        <div>
          <select
            name="subject"
            value={form.subject}
            onChange={handleChange}
            className="border border-gray-300 rounded px-4 py-2 w-full"
          >
            <option value="">Select Subject</option>
            <option value="General Inquiry">General Inquiry</option>
            <option value="Technical Support">Technical Support</option>
            <option value="Billing">Billing</option>
          </select>
          {renderError('subject')}
        </div>

        <div>
          <textarea
            name="message"
            placeholder="Your Message"
            value={form.message}
            onChange={handleChange}
            rows="4"
            className="border border-gray-300 rounded px-4 py-2 w-full"
          />
          {renderError('message')}
        </div>

        <p className="text-sm text-gray-500">
          Our support team will get back to you within 24 hours.
        </p>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-[#FF7236] hover:bg-[#EB5E22] text-white font-medium py-2 rounded transition duration-200 disabled:opacity-50"
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </button>

        {submitted && (
          <div className="text-green-600 text-sm mt-2">
            ✅ Message submitted successfully!
          </div>
        )}
        {error && (
          <div className="text-red-600 text-sm mt-2">
            ⚠️ {error}
          </div>
        )}
      </form>
    </AuthForm>
  );
}
